import { TrackedEvents, trackEvent } from 'analytics';
import type { SampleVariant, SiteMaterialSampleWithBreakdown } from 'api';
import { formatDate } from 'date';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  DataCard,
  ExpandableIconButton,
  formatNumber,
  type SelectChangeEvent,
  Typography,
  useBreakpoint,
} from 'ui';
import { ImageOutlined, ScaleOutlined } from 'ui/icons';

import { MaterialSampleBreakdown } from '../../../breakdowns';
import { SampleSelect } from '../../../quality-report';

export const WeightSampleAnalysisDataCard = ({
  isLoading,
  samples,
  sampleMaterial,
  hasMedia,
}: {
  isLoading?: boolean;
  samples: readonly SiteMaterialSampleWithBreakdown[];
  sampleMaterial?: SampleVariant;
  hasMedia: boolean;
}) => {
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();

  const handleClickMedia = () => {
    trackEvent(TrackedEvents.QUALITY_REPORT_CLICK_CATEGORY_IMAGES, {
      category: 'Manual Sample Analysis',
    });

    const searchParams = new URLSearchParams();
    searchParams.set('tab', 'sample');
    if (selectedSample) {
      searchParams.set('sampleId', selectedSample.id.toString());
    }

    navigate({
      pathname: 'media',
      search: searchParams.toString(),
    });
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const sampleId = searchParams.get('sampleId');

  const [selectedSample, setSelectedSample] = useState<SiteMaterialSampleWithBreakdown | null>(samples?.[0] ?? null);

  const totalWeightInKg = formatNumber(Number(selectedSample?.total_weight), {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    style: 'kilogram',
  });

  const handleChangeSample = (event: SelectChangeEvent<string>) => {
    const selected = samples?.find((sample) => sample?.id === Number(event.target.value));
    if (selected) {
      setSelectedSample(selected);
      setSearchParams({ sampleId: selected.id.toString() }, { replace: true });
    }
  };

  useEffect(() => {
    if (!samples?.length) {
      return;
    }

    if (!sampleId) {
      setSelectedSample(samples?.[0]);
      return;
    }

    const selected = samples?.find((sample) => sample?.id === Number(sampleId));
    if (selected) setSelectedSample(selected);
  }, [samples, sampleId]);

  return (
    <DataCard
      isLoading={isLoading}
      slotProps={{
        header: {
          action: hasMedia ? (
            <ExpandableIconButton icon={<ImageOutlined />} label="Show media" onClick={handleClickMedia} />
          ) : undefined,
          avatar: <ScaleOutlined />,
          title: 'Manual Sample Analysis',
        },
      }}
    >
      <DataCard.Item colSpan={isMobile ? 12 : 6}>
        <Typography
          variant="body1"
          color="textSecondary"
          sx={{
            paddingBottom: 2,
          }}
        >
          {selectedSample?.total_weight ? (
            <>
              Data is based on the most recent manual sample of {totalWeightInKg} of supplier output material, completed
              by a Safi inspector. Values are rounded to nearest 0.01 kg.
            </>
          ) : (
            <>Values are rounded to nearest 0.01 kg.</>
          )}
        </Typography>
      </DataCard.Item>

      {samples ? (
        <DataCard.Item title={samples.length === 1 ? 'Latest inspection' : ''} colSpan={isMobile ? 12 : 6}>
          {samples.length === 1 ? (
            formatDate(samples[0].date)
          ) : (
            <SampleSelect selectedSample={selectedSample} handleChangeSample={handleChangeSample} samples={samples} />
          )}
        </DataCard.Item>
      ) : null}

      <Box mb={2} width="100%">
        <MaterialSampleBreakdown
          items={(selectedSample?.breakdown ?? []).map((item) => ({
            label: item.name_display,
            unit: item.unit,
            unit_display: item.unit_display,
            value: item.value,
          }))}
          variant={sampleMaterial}
          precision={2}
        />
      </Box>
    </DataCard>
  );
};
